import { Modal, ModalOverlay, ModalContent } from "@chakra-ui/react";
import { translate } from "ui/i18n";
import { useWindowWidth } from "ui/utils/hooks";
import { CloseButton } from "ui/pages/app/components/buttons/CloseButton";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import {
  selectFolders,
  selectIsFoldersLoading,
} from "domain/folders/folders.selectors";
import {
  addSeanceToFolder,
  getFolders,
  removeSeanceFromFolder,
} from "domain/folders/folders.actions";
import { useEffect, useState } from "react";
import { PrimaryButton } from "ui/pages/app/components/buttons/PrimaryButton";
import { INCheckbox } from "ui/pages/app/components/inputs/INCheckbox";
import { Folder } from "domain/entities/folders.entities";
import { Snackbar } from "ui/pages/snackbar";
import { ModalManageFolder } from "ui/pages/app/my-creations/components/ModalManageFolder";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  folders?: Folder[];
  seanceId: number;
  seanceFolders: { folderId?: number; folderSeanceId?: number }[];
}

export function ModalChooseFolder({
  isOpen,
  onClose,
  folders,
  seanceId,
  seanceFolders,
}: IProps) {
  const isDesktop = useWindowWidth();
  const isLoading = useAppSelector(selectIsFoldersLoading);
  const reducerFolders = useAppSelector(selectFolders);
  const dispatch = useAppDispatch();
  const [selectedFolders, setSelectedFolders] = useState<number[]>([]);
  const [removeFromFolders, setRemoveFromFolders] = useState<
    { folderId: number; folderSeanceId: number }[]
  >([]);

  useEffect(() => {
    fetchFolders();
  }, []); //eslint-disable-line

  useEffect(() => {
    if (seanceFolders) {
      setSelectedFolders(seanceFolders.map((folder) => folder.folderId));
    }
  }, [seanceFolders]);

  const fetchFolders = async () => {
    if (!folders) {
      await dispatch(getFolders());
    }
  };

  const handleCheckboxClick = (folderId: number) => {
    const seanceFolder = seanceFolders.find((f) => f.folderId === folderId);
    if (seanceFolder) {
      setRemoveFromFolders((prev) =>
        prev.find((f) => f.folderId === folderId)
          ? prev.filter((f) => f.folderId !== folderId)
          : [...prev, { folderId, folderSeanceId: seanceFolder.folderSeanceId }]
      );
    }
    setSelectedFolders((prev) =>
      prev.includes(folderId)
        ? prev.filter((id) => id !== folderId)
        : [...prev, folderId]
    );
  };

  const handleValidate = () => {
    selectedFolders
      .filter((id) => !seanceFolders.find((f) => f.folderId === id))
      .forEach(async (folderId, index) => {
        const result = await dispatch(
          addSeanceToFolder({ folderId, seanceId })
        ).unwrap();
        if (result.success) {
          Snackbar.showSuccess({
            title: translate("ns1:Folders.AddedToFolder"),
          });
        }
      });
    removeFromFolders.forEach(async (folder, index) => {
      const result = await dispatch(removeSeanceFromFolder(folder)).unwrap();
      if (result.success) {
        Snackbar.showSuccess({
          title: translate("ns1:Folders.RemovedFromFolder"),
        });
      }
    });
    setRemoveFromFolders([]);
    onClose();
  };

  const closeModalFolder = () => {
    fetchFolders();
    onClose();
  };

  return reducerFolders?.length > 0 || folders?.length > 0 ? (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent maxW="750px" margin="20px" className="!rounded-[30px] p-8">
        <CloseButton
          onClick={onClose}
          alternativeText={translate("ns1:Button.Cancel")}
        />
        <div className="mt-12 flex flex-col items-center">
          <h4 className="w-fit text-center font-bold  text-secondaryGrey-900">
            {translate("ns1:Folders.WhichFolders")}
          </h4>
        </div>

        <div className="mx-auto mt-6 w-full max-w-[600px]">
          <div className="max-h-[300px] overflow-y-auto">
            {(folders || reducerFolders).map((folder) => (
              <INCheckbox
                key={folder.id}
                inputName={folder.id.toString()}
                text={folder.label}
                value={selectedFolders.includes(folder.id)}
                onChange={() => handleCheckboxClick(folder.id)}
                className="my-2"
                labelClassName={`text-sm md:text-base !text-darkGrey-700 ${
                  selectedFolders.includes(folder.id) ? "font-bold" : ""
                }`}
              />
            ))}
          </div>

          <PrimaryButton
            height={isDesktop ? "52px" : "46px"}
            width="100%"
            className="mt-6 md:mt-12"
            disabled={isLoading || selectedFolders.length === 0}
            onClick={handleValidate}
          >
            {translate("ns1:Button.Validate")}
          </PrimaryButton>
        </div>
      </ModalContent>
    </Modal>
  ) : (
    <ModalManageFolder
      isOpen={isOpen}
      onClose={closeModalFolder}
      setLastCreatedFolder={(folderId: number) => {
        setSelectedFolders([folderId]);
      }}
      seanceId={seanceId}
    />
  );
}
